import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import {
    Portlet, PortletBody,
    PortletHeader, PortletHeaderToolbar
} from "../../partials/content/Portlet";

import { Box, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { routes } from '../../router/Routes';

import { BreadCrumb } from '../../common/Breadcrumb';
import makeStyles from '@mui/styles/makeStyles';

import { TableLevelAction } from '../../common';
import GetAppIcon from '@mui/icons-material/GetApp';
import { PurePtr, useStatePtr } from '@pure-ptr/react';
import { ChargeAnalysisGrid } from './ChargeAnalysisGrid';
import { pageStore, useGlobalStore, pageGridFilters, SearchTitle } from '../../common';
import { SEARCH_PERIOD_ID } from '../../store/constants/settings';
import { useFitHeight } from '../../../_metronic/layout/Layout';
import {ChargeAnalysisTopFilters} from './ChargeAnalysisTopFilters';
import { ChargeStatistics } from './ChargeStatistics';
import { ClaimAdjustments } from '../ClaimAnalysisPage/ClaimViewPage/ClaimAdjustments';

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
        zIndex: 100,
    },
    formControl: {
        margin: theme.spacing(1),
        width: 200,
    },
    formControlClaim: {
        margin: theme.spacing(1),
        width: 71
    },
    formControlStatus: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: 4,
        marginRight: 4,
        width: 140,
        '@media (max-width: 1440px)': {
            width: 110,
        }
    },
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        pointerEvents: 'auto',
        width: 300,
    },
    switch: {
        marginRight: 0,
        marginTop: 20,
        marginLeft: 0
    },
    gridMDMargin: {
        '@media (max-width: 1023px)': {
            paddingLeft: 10,
        }
    },
    IconButton: {
        padding: 6
    },
    statisticsContainer:{
        padding:'20px',
        paddingBottom:0
    }
}));

const { usePageStore, useGridFilters } = pageStore('charge-analysis-landing', {
    sort: [{ id: 'claimnumber', direction: 'asc' }]
});

export default function ChargeAnalysisPage({
    intl,
    defaultCustomFilters, 
    selectedClientName,
    pending,
    claimId,
    actions: {
        chargeAnalysisLanding,
        downloadAnalysisReport,
        searchGetByName,
        searchReset,
        resetPagedTable,
        setMulti,
        resetCriteriaWaiting,
        getTinyContracts,
        getFacilities,
        setClaimId
    }
} ) {
    useFitHeight();
    const isSummary= window.location.href.includes("fromSummary")

    const getByNamePending = useGlobalStore( x => x.search.getByName.pending );

    const chargeAnalysisDownloadReportPending = useGlobalStore( x => x.chargeAnalysis.download.pending );

    const claimsSearchTerm = usePageStore( x => x.searchTerm );


    const claimsData = usePageStore((x) => x.list);
    const significance = usePageStore( x => x.significance );

    const relativeFilter = useGlobalStore( x => 
        x.settings.data.find( setting => 
            setting.id === SEARCH_PERIOD_ID
        )
        ?.value ?? '' 
    );

    const classes = useStyles();
    const firstUpdate = useRef(true);
    const [refresh, setRefresh] = useState(Promise.resolve());

    useEffect(() => {
        getTinyContracts();
    }, [getTinyContracts]);

    useEffect(() => {
        getFacilities();
    }, [getFacilities]);

    useEffect(() => {
        if(isSummary) {
            resetCriteriaWaiting()
            return;
        } //We do not need any criteria filters if we are fetching based on summary filters//         
        const pageName = {
            name: 'charge-analysis-landing'
        }
        searchGetByName(pageName);

        return function cleanup() {
            resetPagedTable();
            searchReset();
        }
    }, [searchGetByName]);


    function handleLoad(sort, filter, pagination) {

        let newOperands=filter?.operands?.map(operand => {
            const newFilters = operand?.filters?.map(filter => {
                if (filter.id === 'contractName' && filter.value.includes(',') && filter.equator.includes('equal')) {
                    return {
                        ...filter,
                        value: `"${filter.value}"`
                    };
                }
                return filter;
            });
            return {
                ...operand,
                filters: newFilters
            };
        });
        // @ts-ignore
        let commaEscapedFilters={...filter,operands:newOperands} || null;
        chargeAnalysisLanding(pagination, sort,!filter ? null : commaEscapedFilters, claimsSearchTerm?.length > 0 ? false:significance);
    }

    const tableFilter = useGridFilters(),
        claimPageRecord = tableFilter.pagination;

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }

    function handleDowloadTemplate() {
        let operandsWithoutBoth = tableFilter.filters.operands.filter(operand =>
            !operand.filters.some(filter => filter.value === 'both')
        );
        let filterWithoutBoth = {
            ...tableFilter.filters,
            operands: operandsWithoutBoth
        };
        const payload = {
            page: null,
            filter: filterWithoutBoth || null,
            sort: tableFilter.sort || [],
        };
        downloadAnalysisReport(payload,significance, downloadTemplateCallback);
    }

    function downloadTemplateCallback(blob) {
        const displayName = `${selectedClientName.replace(/ /g, "_")}_ChargeAnalysisReport`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = `${displayName.replace(/\s/g, '')}.csv`;
        a.click();
    }

    useLayoutEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        handleRefresh();
    }, [significance]);

    function handleFilterClear() {
        
            setMulti({
            filter: [],
            selectedFilter: null,
            customFilter: defaultCustomFilters,
            list: [],
            significance:true,
            searchTerm:"",
            });
            handleRefresh()
    }

    const selectedClaimsIdsPtr = useStatePtr<number[]>([]);

    return (
        <>
            <Helmet title="Charge Analysis" />
            <Grid container sx={{ position: "relative" }}>

                <Grid
                    sx={{
                        position: "absolute",
                        right: "10px",
                    }}
                >
                    <SearchTitle
                        title="Charge Analysis"
                        name={"charge-analysis-landing"}
                        handleRefresh={handleRefresh}
                        shouldNotSetDefault={false}
                        showSearchCriteriaDropdown={false}
                    />
                </Grid>
            </Grid>
            <Portlet>
                <Box className={classes.statisticsContainer}>
                    <ChargeStatistics />
                </Box>
                <PortletHeader
                    title="Charge Analysis"
                    name="charge-analysis-landing"
                    handleRefresh={handleRefresh}
                    showSearchFilters={false}
                    handleFilterClear={handleFilterClear}
                    includeAdvanceFilters={true}
                    toolbar={
                        <PortletHeaderToolbar>
                            <ChargeAnalysisTopFilters handleRefresh={handleRefresh} relativeFilter={relativeFilter} />
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody>
                    <Stack direction="column" spacing={1} sx={{ height: '100%', minHeight: 280 }}>
                        <TableLevelAction
                            handleRefresh={handleRefresh}
                            actions={ () =>
                                <>
                                    <Grid item>
                                        <Tooltip title="Export">
                                            <IconButton aria-label="export" size='small' 
                                                onClick={handleDowloadTemplate} 
                                                disabled={chargeAnalysisDownloadReportPending}
                                            >
                                                <GetAppIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </>
                            }
                            
                            loading={chargeAnalysisDownloadReportPending}
                            actionLevelGrid={ () =>
                                <Grid container>
                                    <Grid item md={12}>
                                    <h6 className="">{`Total Records : ${(claimsData?.length > 0 && claimPageRecord?.totalRecords) ? claimPageRecord?.totalRecords : 0}`}</h6>
                                    </Grid>
                                </Grid>
                            }
                        />

                        <ChargeAnalysisGrid
                            defaultCustomFilter={defaultCustomFilters?.filter(x => x.field !== 'acknowledged' && x.field !== 'flag' && x.field !== 'contract.contractState.name' && x.field !== 'servicedate')}
                            selectedClaimsIdsPtr={selectedClaimsIdsPtr}
                            data={claimsData}
                            loading={ pending || getByNamePending }

                            paginationFilters={tableFilter}
                            onLoad={handleLoad}
                            refresh={refresh}
                            showSearchFilters={true}
                            setClaimId={setClaimId}
                        />
                    </Stack>

                </PortletBody>
            </Portlet>
            <ClaimAdjustments claimId={claimId}/>
        </>
    );
}